import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import usePushNotifications from '../hooks/usePushNotifications';

const SENTIMENT_COLOR = { angry: '#ef4444', sad: '#f97316' };

export default function NotificationBell() {
    const [notifications, setNotifications] = useState([]);
    const [unread, setUnread]               = useState(0);
    const [open, setOpen]                   = useState(false);
    const [loading, setLoading]             = useState(false);
    const ref = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/notifications');
            setNotifications(data.data);
            setUnread(data.unreadCount);
        } catch {}
        finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        const interval = setInterval(load, 60000);
        return () => clearInterval(interval);
    }, []);

    // Sync PWA icon badge with unread count
    useEffect(() => {
        if ('setAppBadge' in navigator) {
            if (unread > 0) {
                navigator.setAppBadge(unread).catch(() => {});
            } else {
                navigator.clearAppBadge().catch(() => {});
            }
        }
    }, [unread]);

    // Clear badge when app gains focus
    useEffect(() => {
        const handleFocus = () => {
            if ('clearAppBadge' in navigator) navigator.clearAppBadge().catch(() => {});
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, []);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleOpen = () => { setOpen(o => !o); };

    const markRead = async (id) => {
        try {
            await api.put(`/notifications/${id}/read`);
            setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
            setUnread(u => Math.max(0, u - 1));
        } catch {}
    };

    const markAllRead = async () => {
        try {
            await api.put('/notifications/read-all');
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnread(0);
            if ('clearAppBadge' in navigator) navigator.clearAppBadge().catch(() => {});
        } catch {}
    };

    const timeAgo = (date) => {
        const s = Math.floor((Date.now() - new Date(date)) / 1000);
        if (s < 60)    return 'just now';
        if (s < 3600)  return `${Math.floor(s/60)}m ago`;
        if (s < 86400) return `${Math.floor(s/3600)}h ago`;
        return `${Math.floor(s/86400)}d ago`;
    };

    const push = usePushNotifications();
    const navigate = useNavigate();

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <style>{`
                .notif-dropdown {
                    position: absolute;
                    top: calc(100% + 8px);
                    right: 0;
                    width: 340px;
                }
                @media (max-width: 480px) {
                    .notif-dropdown {
                        position: fixed !important;
                        top: calc(60px + env(safe-area-inset-top)) !important;
                        left: 16px !important;
                        right: 16px !important;
                        width: calc(100vw - 32px) !important;
                        max-width: none !important;
                    }
                }
            `}</style>

            {/* Bell button */}
            <button
                onClick={handleOpen}
                style={{
                    position: 'relative', background: 'none', border: 'none',
                    cursor: 'pointer', padding: '6px 8px', borderRadius: 8,
                    transition: 'background .2s', color: 'var(--text-muted)',
                }}
                onMouseOver={e => e.currentTarget.style.background = 'var(--bg)'}
                onMouseOut={e => e.currentTarget.style.background = 'none'}
                title="Notifications"
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {unread > 0 && (
                    <span style={{
                        position: 'absolute', top: 4, right: 4,
                        width: 16, height: 16, borderRadius: '50%',
                        background: '#ef4444', color: '#fff',
                        fontSize: 10, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '2px solid var(--surface)',
                    }}>
                        {unread > 9 ? '9+' : unread}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {open && (
                <div
                    className="notif-dropdown"
                    style={{
                        maxHeight: 420, overflowY: 'auto',
                        background: 'var(--surface)', border: '1px solid var(--border)',
                        borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        zIndex: 9999,
                    }}
                >
                    {/* Header */}
                    <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: push.isSupported ? 10 : 0 }}>
                            <span style={{ fontWeight: 700, fontSize: 14 }}>Notifications</span>
                            {unread > 0 && (
                                <button onClick={markAllRead} style={{ fontSize: 12, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>
                                    Mark all read
                                </button>
                            )}
                        </div>

                        {/* Push toggle */}
                        {push.isSupported && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg)', borderRadius: 8, padding: '8px 10px', gap: 8 }}>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                        {push.subscribed ? '🔔 Device alerts on' : '🔕 Device alerts off'}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                        {push.subscribed ? "You'll get notified on this device" : 'Get notified when feedback is angry or sad'}
                                    </div>
                                </div>
                                <button
                                    onClick={push.subscribed ? push.unsubscribe : push.subscribe}
                                    disabled={push.loading}
                                    style={{
                                        padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                        border: 'none',
                                        background: push.subscribed ? '#fee2e2' : 'var(--primary)',
                                        color: push.subscribed ? '#b91c1c' : '#fff',
                                        flexShrink: 0,
                                    }}
                                >
                                    {push.loading ? '…' : push.subscribed ? 'Turn off' : 'Turn on'}
                                </button>
                            </div>
                        )}
                        {push.error && <p style={{ fontSize: 11, color: '#ef4444', margin: '6px 0 0' }}>{push.error}</p>}
                    </div>

                    {/* List */}
                    {loading && notifications.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center' }}><div className="spinner" /></div>
                    ) : notifications.length === 0 ? (
                        <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                            <div style={{ fontSize: 32, marginBottom: 8 }}>🔔</div>
                            No notifications yet
                        </div>
                    ) : (
                        notifications.map(n => (
                            <div
                                key={n._id}
                                onClick={() => {
                                    if (!n.read) markRead(n._id);
                                    if (n.data?.responseId) {
                                        setOpen(false);
                                        navigate(`/tenant/dashboard?tab=responses&responseId=${n.data.responseId}`);
                                    }
                                }}
                                style={{
                                    display: 'flex', gap: 12, padding: '12px 16px',
                                    borderBottom: '1px solid var(--border)',
                                    background: n.read ? 'transparent' : 'rgba(239,68,68,0.04)',
                                    cursor: (n.data?.responseId || !n.read) ? 'pointer' : 'default',
                                    transition: 'background .15s',
                                }}
                                onMouseOver={e => { if (!n.read || n.data?.responseId) e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                                onMouseOut={e => { e.currentTarget.style.background = n.read ? 'transparent' : 'rgba(239,68,68,0.04)'; }}
                            >
                                <div style={{
                                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                    background: `${SENTIMENT_COLOR[n.data?.sentiment] || '#ef4444'}15`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 18,
                                }}>
                                    {n.title.split(' ')[0]}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: n.read ? 500 : 700, color: 'var(--text)', marginBottom: 2 }}>{n.title}</div>
                                    <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.message}</div>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{timeAgo(n.createdAt)}</div>
                                </div>
                                {!n.read && (
                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', flexShrink: 0, marginTop: 4 }} />
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}